import { replaceVariablesInMessage } from '@chatwoot/utils';
import { buildMessageVariables } from './messageVariables';
import { SIGNATURE_LOCATIONS } from './editorHelper';

/**
 * Where the signature content comes from when the inbox forces a signature.
 * - `inbox`: the signature configured on the inbox settings
 * - `agent`: the signature each agent configured on their own profile
 */
export const SIGNATURE_SOURCES = Object.freeze({
  AGENT: 'agent',
  INBOX: 'inbox',
});

/**
 * Whether the inbox forces the signature on every outgoing message.
 *
 * @param {Object} inbox
 * @returns {boolean}
 */
export const isSignatureForcedByInbox = inbox =>
  !!inbox?.personal_signature_enabled;

/**
 * Resolves which signature should be used for the given inbox.
 * When the inbox does not force a signature, the agent signature is used.
 *
 * @param {Object} inbox
 * @returns {string} one of SIGNATURE_SOURCES
 */
export const getSignatureSource = inbox => {
  if (!isSignatureForcedByInbox(inbox)) {
    return SIGNATURE_SOURCES.AGENT;
  }

  return inbox?.personal_signature_source === SIGNATURE_SOURCES.INBOX
    ? SIGNATURE_SOURCES.INBOX
    : SIGNATURE_SOURCES.AGENT;
};

/**
 * Resolves where the signature should be placed.
 * The inbox overrides the agent preference when it forces the signature.
 *
 * @param {Object} params
 * @param {Object} params.inbox
 * @param {string} params.agentSignaturePosition - the agent preference
 * @returns {string} one of SIGNATURE_LOCATIONS
 */
export const getSignaturePosition = ({ inbox, agentSignaturePosition }) => {
  if (isSignatureForcedByInbox(inbox)) {
    return inbox?.personal_signature_position || SIGNATURE_LOCATIONS.END;
  }

  return agentSignaturePosition || SIGNATURE_LOCATIONS.END;
};

/**
 * Returns the signature content before any variable is resolved.
 *
 * @param {Object} params
 * @param {Object} params.inbox
 * @param {string} params.agentSignature - the signature configured by the agent
 * @returns {string}
 */
export const getRawSignature = ({ inbox, agentSignature }) => {
  if (getSignatureSource(inbox) === SIGNATURE_SOURCES.INBOX) {
    return inbox?.personal_signature_body || '';
  }

  return agentSignature || '';
};

/**
 * Builds the signature that will be appended to the message, with every system
 * variable already replaced.
 *
 * `agent.*` variables always resolve to the user sending the message, not to the
 * conversation assignee.
 *
 * @param {Object} params
 * @param {Object} params.inbox
 * @param {string} params.agentSignature - the signature configured by the agent
 * @param {Object} params.currentUser - the user sending the message
 * @param {Object} params.contact
 * @param {Object} params.conversation
 * @returns {string}
 */
export const resolveSignature = ({
  inbox,
  agentSignature,
  currentUser,
  contact,
  conversation,
}) => {
  const rawSignature = getRawSignature({ inbox, agentSignature });

  if (!rawSignature) {
    return '';
  }

  const variables = buildMessageVariables({
    contact,
    agent: currentUser,
    conversation,
    inbox,
  });

  return replaceVariablesInMessage({ message: rawSignature, variables });
};
